import { css } from "@emotion/react"
import { useQuery } from "@tanstack/react-query"
import { omit } from "lodash"
import { useEffect } from "react"
import { useDonationFormContext } from "../hooks/useDonationFormContext"
import { fetchStartDates } from "../queries"
import {
  DateStampString,
  EditableFrequency,
  EveryOtherWeekFrequency,
  MonthDayNumber,
  MonthlyFrequency,
  TwiceMonthlyDays,
  TwiceMonthlyFrequency,
  WeekDayNumber,
  WeeklyFrequency,
} from "../types"
import { FREQUENCY_PREFIX_TEXT } from "../utils/frequency"
import {
  DaySelector,
  StartingSelector,
  TwiceMonthlySelector,
  WeekDaySelector,
} from "./FrequencyDateSelectors"

export function FrequencyEditable({
  frequency,
}: {
  frequency: EditableFrequency
}) {
  const { dispatch } = useDonationFormContext()

  const frequencyQueryKey = omit(frequency, "starting")
  const { data: options } = useQuery({
    queryKey: ["DonationFormSchedules", frequencyQueryKey],
    queryFn: () => fetchStartDates(frequency),
  })

  // Because we want to default to the first option of a select list that just
  // changed, we need to reselect the first item in the list if we haven't
  // already.
  useEffect(() => {
    options && dispatch({ type: "REFRESH_FREQUENCY_STARTING", options })
  }, [options, dispatch])

  const handleStartingChange = (starting: DateStampString) => {
    dispatch({
      type: "UPDATE_FREQUENCY",
      frequency: { ...frequency, starting },
    })
  }

  const handleConfirm = () => dispatch({ type: "COLLAPSE_EDIT_FREQUENCY" })

  return (
    <div className="d-f fd-c g-1 pt-1">
      <div
        css={css`
          display: grid;
          grid-template-columns: auto minmax(120px, 1fr);
          column-gap: clamp(0.5rem, 4vw, 3rem);
          row-gap: 0.5rem;
          align-items: center;
        `}
      >
        {frequency.type === "monthly" && <MonthlyForm frequency={frequency} />}
        {frequency.type === "weekly" && <WeeklyForm frequency={frequency} />}
        {frequency.type === "every_other_week" && (
          <EveryOtherWeeklyForm frequency={frequency} />
        )}
        {frequency.type === "twice_monthly" && (
          <TwiceMonthlyForm frequency={frequency} />
        )}
        <>
          <span>Starting </span>
          <StartingSelector
            options={options || []}
            value={frequency.starting}
            onDateChange={handleStartingChange}
            isLoading={!options}
          />
        </>
      </div>
      <div className="d-f jc-fe ai-c g-2">
        <button
          type="button"
          className="btn link-btn px-1 br-4"
          onClick={handleConfirm}
        >
          Okay
        </button>
      </div>
    </div>
  )
}

function MonthlyForm({ frequency }: { frequency: MonthlyFrequency }) {
  const { dispatch } = useDonationFormContext()
  const handleDayChange = (day: MonthDayNumber) => {
    dispatch({ type: "UPDATE_FREQUENCY", frequency: { ...frequency, day } })
  }
  return (
    <>
      {FREQUENCY_PREFIX_TEXT["monthly"]}{" "}
      <DaySelector value={frequency.day} onDayChange={handleDayChange} />
    </>
  )
}

function WeeklyForm({ frequency }: { frequency: WeeklyFrequency }) {
  const { dispatch } = useDonationFormContext()
  const handleWeekDayChange = (weekday: WeekDayNumber) => {
    dispatch({ type: "UPDATE_FREQUENCY", frequency: { ...frequency, weekday } })
  }
  return (
    <>
      {FREQUENCY_PREFIX_TEXT["weekly"]}{" "}
      <WeekDaySelector
        value={frequency.weekday}
        onWeekDayChange={handleWeekDayChange}
      />
    </>
  )
}

function EveryOtherWeeklyForm({
  frequency,
}: {
  frequency: EveryOtherWeekFrequency
}) {
  const { dispatch } = useDonationFormContext()
  const handleWeekDayChange = (weekday: WeekDayNumber) => {
    dispatch({ type: "UPDATE_FREQUENCY", frequency: { ...frequency, weekday } })
  }
  return (
    <>
      {FREQUENCY_PREFIX_TEXT["every_other_week"]}{" "}
      <WeekDaySelector
        value={frequency.weekday}
        onWeekDayChange={handleWeekDayChange}
      />
    </>
  )
}

function TwiceMonthlyForm({ frequency }: { frequency: TwiceMonthlyFrequency }) {
  const { dispatch } = useDonationFormContext()
  const handleDaysChange = (days: TwiceMonthlyDays) => {
    dispatch({ type: "UPDATE_FREQUENCY", frequency: { ...frequency, days } })
  }
  return (
    <>
      {FREQUENCY_PREFIX_TEXT["twice_monthly"]}{" "}
      <TwiceMonthlySelector
        value={frequency.days}
        onDaysChange={handleDaysChange}
      />
    </>
  )
}
